import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Popper } from '@mui/material';
import {
    FindLoad,
    FindSuccessItem,
    goalDataTypeEnums,
    Images,
    PermissionList,
    renderTargetStatuses,
    RolePermission,
    targetStatusEnums,
} from 'utils';
import { CloseButton, MinLoader, SimpleTooltip } from 'components';
import { goalActions, httpRequestsOnSuccessActions } from 'store';

const ACTION_TYPE = 'EDIT_TARGET';

const renderStatusConfig = (status) => {
    const config = renderTargetStatuses.find((s) => s?.value === status) || {
        backgroundColor: '',
        circleColor: '',
        color: '',
        label: '',
        filter: '',
    };
    return { ...config };
};

const dataTypesWithoutOptions = [
    goalDataTypeEnums.DURATION,
    goalDataTypeEnums.FREQUENCY,
    goalDataTypeEnums.SCORE,
    goalDataTypeEnums.RATE,
    goalDataTypeEnums.IRT,
];

/**
 * Tooltip for a pending target's status control when its goal collects data
 * against options but has none: the status cannot leave PENDING until an
 * option is added. '' otherwise, so no tooltip shows.
 */
export const targetOptionsTooltip = (goalById, status) => {
    return status === targetStatusEnums.PENDING && isTargetStatusLocked(goalById) ? (
        <p>Options are required for data collection. Add at least one option to this goal before changing the target's status.</p>
    ) : (
        ''
    );
};

/** True when a goal collects data against options but has none: its targets' status cannot change yet. */
export const isTargetStatusLocked = (goal) => !dataTypesWithoutOptions.includes(goal?.dataType) && !goal?.options?.length;

/** Who may change a target's status: GOAL_CREATE (same as editing targets on the Targets tab), never on a deleted goal. */
export const canEditTargetStatus = (goal) => !goal?.deletedAt && RolePermission([PermissionList.GOAL_CREATE?.code]);

/** Tooltip for a mastered target's status: whether mastery was automatic or manual (latest status history entry). */
export const MasteredTargetTooltip = ({ target, children }) => {
    const lastHistory = target?.statusHistory?.length ? target.statusHistory[target.statusHistory.length - 1] : {};
    if (target?.status !== targetStatusEnums.MASTERED) return children;
    return (
        <SimpleTooltip
            title={
                <div>
                    <p style={{ fontWeight: 600, marginBottom: 6 }}>{lastHistory?.automatic ? 'Mastered automatically' : 'Mastered manually'}</p>
                    <p>
                        {lastHistory?.automatic
                            ? 'This target was mastered automatically based on maintenance rule.'
                            : 'This target was mastered manually by the supervising therapist.'}
                    </p>
                </div>
            }
            placement="top"
            arrow>
            <div>{children}</div>
        </SimpleTooltip>
    );
};

export const GoalTargetStatus = ({ target, goalById, requestParams, noAction: noActionProp, onSuccess }) => {
    const dispatch = useDispatch();
    // The permission gate lives here so no caller can render an editable status for a user without it.
    const noAction = noActionProp || !canEditTargetStatus(goalById);
    const [anchorEl, setAnchorEl] = useState(null);
    const openStatus = Boolean(anchorEl);
    const id = openStatus ? 'goal-target-status-popper' : undefined;
    const [pendingStatus, setPendingStatus] = useState('');
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);
    const current = renderStatusConfig(target?.status);
    const statuses = renderTargetStatuses.map((s) => renderStatusConfig(s?.value));

    useEffect(() => {
        if (success?.type === ACTION_TYPE && pendingStatus) {
            setAnchorEl(null);
            setPendingStatus('');
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            onSuccess?.();
        }
    }, [success]);

    const handleClick = (event) => {
        if (noAction) return;
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleChangeStatus = (nextStatus) => {
        if (!goalById?.id || !target?.id) return;
        if (nextStatus === target?.status) {
            setAnchorEl(null);
            return;
        }
        const params = {
            ...target,
            status: nextStatus,
            goal: goalById?.id,
        };
        delete params?.id;
        delete params?.updatedAt;
        delete params?.createdAt;

        setPendingStatus(nextStatus);
        dispatch(goalActions.editTarget(goalById?.id, target?.id, params, requestParams));
    };

    return (
        <div>
            <SimpleTooltip title={targetOptionsTooltip(goalById, target?.status)} placement="bottom" arrow>
                <button
                    aria-describedby={id}
                    onClick={handleClick}
                    className="status-card-box"
                    style={{ backgroundColor: current?.backgroundColor, border: 'none', height: 28, cursor: noAction ? 'default' : 'pointer' }}
                >
                    {current?.label && <div className="status-card-circle" style={{ backgroundColor: current?.circleColor }} />}
                    <p style={{ color: current?.color }}>{current?.label ? current?.label : '--'}</p>
                    {!noAction && <img style={{ filter: current?.filter }} src={Images.dropdownArrow} alt="" />}
                </button>
            </SimpleTooltip>

            <Popper
                id={id}
                open={openStatus}
                anchorEl={anchorEl}
                className="submitted-claim-status-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 1500 }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <div>
                    <div className="submitted-claim-status-header">
                        <p>Update Status</p>
                        <CloseButton handleCLic={handleClick} />
                    </div>
                    <div className="submitted-claim-status-body">
                        {statuses?.map((i, j) => (
                            <button
                                key={j}
                                onClick={() => handleChangeStatus(i?.value)}
                                className="status-card-box"
                                style={{ backgroundColor: 'transparent', border: 'none' }}
                            >
                                {pendingStatus === i?.value && loader?.length ? (
                                    <MinLoader height={6} width={6} color={i?.circleColor} margin={'0'} position={'relative'} />
                                ) : (
                                    <div className="status-card-circle" style={{ backgroundColor: i?.circleColor }} />
                                )}
                                <p style={{ color: i?.color }}>{i?.label}</p>
                            </button>
                        ))}
                    </div>
                </div>
            </Popper>
            {openStatus && (
                <div
                    className="beck-drop"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAnchorEl(null);
                    }}
                />
            )}
        </div>
    );
};
